import { useMemo } from 'react';
import useAuth from '@/context/AuthContext';
import useSubscriptions from '@/lib/api/subscriptionManager/hooks/useSubscriptions';
import useSubEngineSubscriptions from '@/lib/api/subscriptionManager/hooks/useSubEngineSubscriptions';
import { Translations } from '@/utils/translations/generateTranslationObject';

export interface UserMenuLinkConfig {
  id: string;
  label: string;
  href: string;
  onClick?: () => void;
}

interface UseUserMenuLinksReturn {
  hardcodedLinks: UserMenuLinkConfig[];
  isLoading: boolean;
}

const LOGOUT_URL = `https://shop.${process.env.NEXT_PUBLIC_BACKEND_DOMAIN}/account/logout`;

export const useUserMenuLinks = (
  translations: Translations,
): UseUserMenuLinksReturn => {
  const { user, signOut } = useAuth();
  const userUuid = user?.attributes?.loveveryId;

  // Fetch PlayKit subscriptions
  const { data: playKitSubscriptions, loading: playKitSubscriptionsLoading } =
    useSubscriptions({
      userUuid: userUuid || '',
    });

  // Fetch ERC subscriptions
  const { data: ercSubscriptionResponse, isLoading: ercSubscriptionsLoading } =
    useSubEngineSubscriptions({
      userUuid: userUuid || '',
      type: 'erc',
    });

  const ercSubscriptions = ercSubscriptionResponse?.subscriptions || [];

  const isLoading = playKitSubscriptionsLoading || ercSubscriptionsLoading;

  const hardcodedLinks = useMemo((): UserMenuLinkConfig[] => {
    if (!user || isLoading) return [];

    const links: UserMenuLinkConfig[] = [];

    // PlayKit Subscriptions - show if subscriptions > 0
    const hasPlayKitSubscriptions = (playKitSubscriptions?.length || 0) > 0;
    if (hasPlayKitSubscriptions) {
      links.push({
        id: 'playkit-subscriptions',
        label: translations.playKitSubscriptions || 'PlayKit Subscriptions',
        href: '/account/subscriptions',
      });
    }

    // Reading Subscriptions - show if ERC subscriptions > 0
    const hasERCSubscriptions = (ercSubscriptions?.length || 0) > 0;
    if (hasERCSubscriptions) {
      links.push({
        id: 'reading-subscriptions',
        label: translations.readingSubscriptions || 'Reading Subscriptions',
        href: '/account/early-reader-club',
      });
    }

    // Subscriber Shop - show if PlayKit subscriptions > 0
    if (hasPlayKitSubscriptions) {
      links.push({
        id: 'subscriber-shop',
        label: translations.subscriberShop || 'Subscriber Shop',
        href: '/account/subscriber-shop',
      });
    }

    // Logout - always show
    links.push({
      id: 'logout',
      label: translations.logout || 'Logout',
      href: '#',
      onClick: async () => {
        await signOut();
        window.location.assign(LOGOUT_URL);
      },
    });

    return links;
  }, [
    user,
    isLoading,
    playKitSubscriptions?.length,
    ercSubscriptions?.length,
    translations,
    signOut,
  ]);

  return {
    hardcodedLinks,
    isLoading,
  };
};
